import React, { useEffect, useState } from 'react';
import { RefreshCw, AlertCircle, Wifi } from 'lucide-react';

/**
 * Network Error Recovery Component
 * Detects blank pages, 404 errors, and network issues
 * Provides recovery options to users
 */
const NetworkErrorRecovery: React.FC = () => {
  const [showRecovery, setShowRecovery] = useState(false);
  const [errorType, setErrorType] = useState<'blank' | '404' | 'chunk-load' | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);

  useEffect(() => {
    // Detect blank page (no content rendered)
    const checkBlankPage = () => {
      const hasContent = document.body.children.length > 0 && 
                         document.body.textContent?.trim().length > 0;
      
      if (!hasContent && document.readyState === 'complete') {
        console.warn('⚠️ Blank page detected');
        setErrorType('blank');
        setShowRecovery(true);
      }
    };

    // Detect chunk loading errors (common with Vite/code-splitting)
    const handleChunkError = (event: ErrorEvent) => {
      const message = event.message || '';
      
      if (message.includes('Loading chunk') || 
          message.includes('Failed to fetch') ||
          message.includes('dynamically imported module')) {
        console.warn('⚠️ Chunk loading error detected');
        setErrorType('chunk-load');
        setShowRecovery(true);
      }
    };

    // Detect 404 errors from console
    const original404Handler = window.console.error;
    window.console.error = function(...args) {
      const errorString = args.join(' ');
      if (errorString.includes('404') || errorString.includes('Not Found')) {
        console.warn('⚠️ 404 error detected');
        setErrorType('404');
        setShowRecovery(true);
      }
      original404Handler.apply(console, args);
    };

    // Check for blank page after load
    if (document.readyState === 'complete') {
      setTimeout(checkBlankPage, 1000);
    } else {
      window.addEventListener('load', () => {
        setTimeout(checkBlankPage, 1000);
      });
    }

    // Listen for chunk errors
    window.addEventListener('error', handleChunkError);

    return () => {
      window.removeEventListener('error', handleChunkError);
      window.console.error = original404Handler;
    };
  }, []);

  const handleReload = () => {
    setIsRecovering(true);
    window.location.reload();
  };

  const handleClearCache = async () => {
    setIsRecovering(true);
    
    // Clear service worker cache
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      console.log('✓ Cache cleared');
    }

    // Clear localStorage (except auth tokens)
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    localStorage.clear();
    if (token) localStorage.setItem('token', token);
    if (user) localStorage.setItem('user', user);

    // Hard reload
    window.location.reload();
  };

  const handleGoHome = () => {
    setIsRecovering(true);
    window.location.href = '/';
  };

  if (!showRecovery) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-900 bg-opacity-95 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
        {/* Icon */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
          <AlertCircle className="w-8 h-8 text-red-600" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
          {errorType === 'blank' && 'Halaman Kosong Terdeteksi'}
          {errorType === '404' && 'Halaman Tidak Ditemukan'}
          {errorType === 'chunk-load' && 'Gagal Memuat Resource'}
        </h2>

        {/* Description */}
        <p className="text-center text-gray-600 mb-6 text-sm">
          {errorType === 'blank' && (
            <>
              Halaman gagal dimuat dengan benar. Ini biasanya terjadi karena:
              <ul className="list-disc list-inside mt-2 text-left">
                <li>Masalah koneksi internet</li>
                <li>Cache browser yang corrupt</li>
                <li>Update aplikasi yang tidak lengkap</li>
              </ul>
            </>
          )}
          {errorType === '404' && (
            <>
              Resource yang diminta tidak ditemukan. Ini bisa terjadi karena:
              <ul className="list-disc list-inside mt-2 text-left">
                <li>URL salah atau sudah tidak ada</li>
                <li>Server sedang maintenance</li>
                <li>Routing configuration error</li>
              </ul>
            </>
          )}
          {errorType === 'chunk-load' && (
            <>
              Gagal memuat sebagian file aplikasi. Ini sering terjadi karena:
              <ul className="list-disc list-inside mt-2 text-left">
                <li>Aplikasi baru saja diupdate</li>
                <li>Cache browser sudah kadaluarsa</li>
                <li>Koneksi internet terputus saat loading</li>
              </ul>
            </>
          )}
        </p>

        {/* Recovery Actions */}
        <div className="space-y-3">
          <button
            onClick={handleReload}
            disabled={isRecovering}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isRecovering ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Memuat Ulang...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Muat Ulang Halaman
              </>
            )}
          </button>

          <button
            onClick={handleClearCache}
            disabled={isRecovering}
            className="w-full flex items-center justify-center gap-2 bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <Wifi className="w-5 h-5" />
            Bersihkan Cache & Reload
          </button>

          <button
            onClick={handleGoHome}
            disabled={isRecovering}
            className="w-full bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Kembali ke Beranda
          </button>
        </div>

        {/* Help Text */}
        <p className="mt-6 text-xs text-center text-gray-500">
          💡 <strong>Tips:</strong> Jika masalah berlanjut, coba:
          <br />
          1) Periksa koneksi internet Anda
          <br />
          2) Gunakan DNS publik (8.8.8.8 atau 1.1.1.1)
          <br />
          3) Hubungi administrator sistem
        </p>
      </div>
    </div>
  );
};

export default NetworkErrorRecovery;
